import { personName } from "@/lib/issues";
import { recordImages } from "@/lib/record-images";
import type {
  ProjectImage,
  ProjectIssue,
  ProjectRecord,
  ProjectUser,
} from "@/types/api";

/** One photo in the issue viewer, from either the record or an update. */
export interface IssuePhoto {
  id: string;
  /** Full size: the annotated render when there is one, else the upload. */
  src: string;
  /** Strip tile; falls back to the full-size image when there is none. */
  thumbnailSrc: string;
  source: "evidence" | "update";
  /** The update's author. Absent on evidence; null when the account is gone. */
  author?: ProjectUser | null;
  /** ISO instant the photo was taken or posted, or null when unknown. */
  date: string | null;
}

function toIssuePhoto(
  image: ProjectImage,
  fields: Pick<IssuePhoto, "source" | "author" | "date">,
): IssuePhoto {
  // The markup is part of what a photo says, so the annotated render wins.
  const src = image.annotated_url ?? image.image;
  return {
    id: image.id,
    src,
    thumbnailSrc: image.thumbnail_url ?? image.thumbnail ?? src,
    ...fields,
  };
}

function timeOf(value: string): number {
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Every photo on an issue, in the order the work happened: the record's photos
 * first, then each update's, oldest update first. The Activity trail runs the
 * other way; this runs chronologically so swiping reads as the story of the fix
 * and "Before" always means the older photo. A photo that appears twice keeps
 * its first position. See [[issue-photo-viewer.spec]].
 */
export function issuePhotos(
  issue: Pick<ProjectIssue, "updates">,
  // The record's photos and its creation time, which dates a photo with no
  // capture time. The issue's own record read carries both.
  record: Pick<ProjectRecord, "images" | "image" | "created_at"> | null | undefined,
): IssuePhoto[] {
  const evidence = record
    ? recordImages(record).map((image) =>
        toIssuePhoto(image, {
          source: "evidence",
          date: image.taken_at ?? record.created_at ?? null,
        }),
      )
    : [];

  // `sort` is stable, so updates posted in the same instant keep server order.
  const updates = [...(issue.updates ?? [])].sort(
    (a, b) => timeOf(a.created_at) - timeOf(b.created_at),
  );
  const posted = updates.flatMap((update) =>
    update.images.map((image) =>
      toIssuePhoto(image, {
        source: "update",
        author: update.author,
        date: update.created_at,
      }),
    ),
  );

  const seen = new Set<string>();
  return [...evidence, ...posted].filter((photo) => {
    if (seen.has(photo.id)) return false;
    seen.add(photo.id);
    return true;
  });
}

/** `14 Aug 2026`, or null when the value is missing or unreadable. */
export function formatPhotoDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** `Evidence · 2 Aug 2026` or `Update · Sam Lee · 14 Aug 2026`. */
export function photoCaption(photo: IssuePhoto): string {
  const parts =
    photo.source === "evidence"
      ? ["Evidence"]
      : ["Update", personName(photo.author)];
  const date = formatPhotoDate(photo.date);
  if (date) parts.push(date);
  return parts.join(" · ");
}

/**
 * The two compared photos as Before and After. The older one, meaning the one
 * earlier in the viewer's chronological list, is always Before, whichever was
 * tapped first.
 */
export function comparePair(
  a: number,
  b: number,
): { before: number; after: number } {
  return a < b ? { before: a, after: b } : { before: b, after: a };
}
